import { useEffect, useState } from "react";
import router, { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { MarketplaceListingView } from "../MarketplaceListingView";
import { DirectoryMap } from "common/elements/DirectoryMap";
import { ListingCard } from "common/elements/ListingCard";
import { Tabs } from "common/elements/Tabs";
import { BodyFrame } from "common/ui/BodyFrame";
import { Button } from "common/ui/Button";
import { DropdownMenu } from "common/ui/DropdownMenu";
import { Label } from "common/ui/Label";
import { Pagination } from "common/ui/Pagination";
import { Paragraph } from "common/ui/Paragraph";
import { SearchBar } from "common/ui/SearchBar";
import { Skeleton } from "common/ui/Skeleton";
import { Stack } from "common/ui/Stack";

import { useDirectoryListings, useUnit } from "common/middleware/hooks";

import styles from "./DirectoryPage.module.scss";

type ViewMode = "gallery" | "summary" | "map";

const SORT_OPTIONS = [
  { id: "newest", sortOrder: "creationDateTimeUtc", asc: false },
  { id: "priceAsc", sortOrder: "price", asc: true },
  { id: "priceDesc", sortOrder: "price", asc: false },
];

export function DirectoryPage() {
  const { t } = useTranslation(["marketplace", "common"]);
  const { query, isReady } = useRouter();

  // Initialize page/pageSize URL params with directory defaults
  useEffect(() => {
    if (!isReady) return;
    if (query.page && query.pageSize) return;

    router.replace({
      query: {
        ...query,
        page: query.page || "0",
        pageSize: query.pageSize || "20",
      },
    });
  }, [query, isReady]);

  // Reset to first page when search changes
  const searchValue = query.search as string;
  useEffect(() => {
    if (!isReady || !query.page) return;
    if (Number(query.page) !== 0) {
      router.replace({ query: { ...query, page: "0" } });
    }
  }, [searchValue]);

  const view = (query.view as ViewMode) || "gallery";
  const currentSort = (query.sort as string) || "newest";
  const selectedListingId = query.listingId ? Number(query.listingId) : null;

  const [bounds, setBounds] = useState<{
    minLatitude: number;
    maxLatitude: number;
    minLongitude: number;
    maxLongitude: number;
  } | null>(null);

  const sortConfig = SORT_OPTIONS.find((s) => s.id === currentSort) || SORT_OPTIONS[0];

  const { listings, totalCount, isLoading } = useDirectoryListings({
    page: view === "map" ? 0 : (Number(query.page) || 0),
    pageSize: view === "map" ? 100 : (Number(query.pageSize) || 20),
    searchString: query.search as string,
    sortOrder: sortConfig.sortOrder,
    ascSortDirection: sortConfig.asc,
    ...(view === "map" && bounds ? bounds : {}),
  });

  const { unit, isUnitLoading } = useUnit(
    selectedListingId ?? 0,
    { shouldFetch: view === "summary" && selectedListingId != null },
  );

  const setView = (newView: ViewMode, listingId?: number) => {
    const newQuery: Record<string, string> = { ...query as Record<string, string>, view: newView };
    if (listingId) {
      newQuery.listingId = String(listingId);
    } else {
      delete newQuery.listingId;
    }
    router.push({ query: newQuery }, undefined, { shallow: true });
  };

  const setSort = (sortId: string) => {
    router.push({ query: { ...query, sort: sortId, page: "0" } });
  };

  const handleCardClick = (listingId: number) => {
    setView("summary", listingId);
  };

  // Summary navigation
  const currentIndex = listings?.findIndex((l) => l.id === selectedListingId) ?? -1;
  const totalItems = totalCount;

  const navigateSummary = (direction: "prev" | "next") => {
    if (!listings) return;

    if (direction === "next" && currentIndex < listings.length - 1) {
      setView("summary", listings[currentIndex + 1].id);
    } else if (direction === "prev" && currentIndex > 0) {
      setView("summary", listings[currentIndex - 1].id);
    }
  };

  const globalIndex = currentIndex >= 0
    ? (Number(query.page) || 0) * (Number(query.pageSize) || 20) + currentIndex + 1
    : 0;

  return (
    <BodyFrame>
      <div className={styles.DirectoryPage}>
        <Tabs
          items={[
            {
              id: "gallery",
              header: t("common:directory.gallery"),
              onClick: () => setView("gallery"),
            },
            {
              id: "summary",
              header: t("common:directory.summary"),
              onClick: () => {
                if ((listings?.length ?? 0) > 0) {
                  setView("summary", listings[0].id);
                }
              },
            },
            {
              id: "map",
              header: t("common:directory.map"),
              onClick: () => setView("map"),
            },
          ]}
          selectedId={view}
        />

        {view !== "summary" && (
          <>
            <div className={styles.Toolbar}>
              {isLoading
                ? <Skeleton width={120} height={20} />
                : <Label variant="l4">
                    {t("common:directory.resultsCount", { count: totalCount })}
                  </Label>
              }
              <DropdownMenu
                selected={{
                  id: currentSort,
                  item: t(`common:directory.sort.${currentSort}`),
                }}
                items={SORT_OPTIONS.map((opt) => ({
                  id: opt.id,
                  name: t(`common:directory.sort.${opt.id}`),
                  onClick: () => setSort(opt.id),
                }))}
                alignRight
              />
            </div>

            <SearchBar
              placeholder={t("common:directory.searchPlaceholder")}
              fullWidth
            />
          </>
        )}

        {view === "gallery" ? (
          <>
            <Pagination totalNumOfItems={totalCount} />

            {isLoading ? (
              <div className={styles.Grid}>
                {[...Array(8)].map((_, idx) => (
                  <Skeleton key={idx} width="100%" height={280} />
                ))}
              </div>
            ) : (listings?.length ?? 0) > 0 ? (
              <div className={styles.Grid}>
                {listings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    onClick={() => handleCardClick(listing.id)}
                    onMakeOffer={() => router.push(`/properties/${listing.id}`)}
                    onRent={() => router.push(`/properties/${listing.id}?mode=rent`)}
                    onChat={() => router.push(`/properties/${listing.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.Empty}>
                <Paragraph variant="p1">
                  {t("common:directory.noResults")}
                </Paragraph>
              </div>
            )}

            <Pagination totalNumOfItems={totalCount} />
          </>
        ) : view === "summary" ? (
          <>
            <div className={styles.SummaryNav}>
              <Button
                variant="secondary"
                noSubmit
                onClick={() => setView("gallery")}
              >
                {t("common:directory.backToGallery")}
              </Button>
              <Stack gap={16} alignItems="center">
                <Button
                  variant="secondary"
                  noSubmit
                  onClick={() => navigateSummary("prev")}
                  disabled={currentIndex <= 0}
                >
                  {t("common:directory.previous")}
                </Button>
                <Label variant="l4">
                  {globalIndex > 0
                    ? t("common:directory.position", {
                        current: globalIndex,
                        total: totalItems,
                      })
                    : ""
                  }
                </Label>
                <Button
                  variant="secondary"
                  noSubmit
                  onClick={() => navigateSummary("next")}
                  disabled={currentIndex >= (listings?.length ?? 0) - 1}
                >
                  {t("common:directory.next")}
                </Button>
              </Stack>
            </div>

            {selectedListingId ? (
              <MarketplaceListingView
                unit={unit}
                isUnitLoading={isUnitLoading}
              />
            ) : (
              <div className={styles.Empty}>
                <Paragraph variant="p1">
                  {t("common:directory.selectListing")}
                </Paragraph>
              </div>
            )}
          </>
        ) : (
          <div className={styles.SplitView}>
            <div className={styles.CardPanel}>
              {isLoading ? (
                [...Array(4)].map((_, idx) => (
                  <Skeleton key={idx} width="100%" height={120} />
                ))
              ) : (listings?.length ?? 0) > 0 ? (
                listings.map((listing) => (
                  <div
                    key={listing.id}
                    className={styles.CardWrapper}
                  >
                    <ListingCard
                      listing={listing}
                      onClick={() => router.push(`/properties/${listing.id}`)}
                      onMakeOffer={() => router.push(`/properties/${listing.id}`)}
                      onRent={() => router.push(`/properties/${listing.id}?mode=rent`)}
                      onChat={() => router.push(`/properties/${listing.id}`)}
                    />
                  </div>
                ))
              ) : (
                <div className={styles.Empty}>
                  <Paragraph variant="p1">
                    {t("common:directory.noResults")}
                  </Paragraph>
                </div>
              )}
            </div>
            <div className={styles.MapContainer}>
              <DirectoryMap
                listings={listings ?? []}
                searchString={query.search as string}
                onBoundsChange={setBounds}
                onMarkerClick={(id) => {
                  router.push(`/properties/${id}`);
                }}
                onMakeOffer={(id) => router.push(`/properties/${id}`)}
                onChat={(id) => router.push(`/properties/${id}`)}
              />
            </div>
          </div>
        )}
      </div>
    </BodyFrame>
  );
}
